import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { baseUrl } from "../../utils/urls";
import AdvisorConditionsPage from "../common/AdvisorConditionsPage";
import { CloudUpload, FileText, Download, CheckCircle2, Clock, XCircle } from "lucide-react";

const getCertificatesUrl = baseUrl + "api/advisor/get-advisor-certificates";
const uploadCertificateUrl = baseUrl + "api/advisor/upload-certificate";

function StatusBadge({ status }) {
  const map = {
    Approved: { cls: "bg-[#D1FADF] text-[#027A48]", icon: CheckCircle2, label: "Approved" },
    Pending: { cls: "bg-[#FEF0C7] text-[#B54708]", icon: Clock, label: "Pending" },
    Rejected: { cls: "bg-[#FEE4E2] text-[#B42318]", icon: XCircle, label: "Rejected" },
  };
  const cfg = map[status] || { cls: "bg-[#F1F5F9] text-[#64748B]", icon: FileText, label: status || "Unknown" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[0.75rem] font-semibold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

function CertSkeleton() {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-[#F1F5F9] rounded w-3/4" />
          <div className="h-3 bg-[#F1F5F9] rounded w-1/2" />
          <div className="h-5 bg-[#F1F5F9] rounded w-20" />
        </div>
      </div>
    </div>
  );
}

const CertificatesPage = () => {
  const loggedInUser = useSelector((s) => s.auth);
  const queryClient = useQueryClient();
  const [dragOver, setDragOver] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [certName, setCertName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["certificates"],
    queryFn: async () => {
      const res = await axios.get(getCertificatesUrl);
      return res.data?.data || [];
    },
    enabled: !loggedInUser?.isSkipLogin && !!loggedInUser?.isAuthenticated,
  });

  const handleUpload = async () => {
    if (!uploadFile || !certName.trim()) {
      setUploadError("Please provide a certificate name and select a file.");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("certificate_name", certName.trim());
      await axios.post(uploadCertificateUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadFile(null);
      setCertName("");
      queryClient.invalidateQueries({ queryKey: ["certificates"] });
    } catch (err) {
      setUploadError(err?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setUploadFile(file);
  };

  if (loggedInUser?.isSkipLogin || !loggedInUser?.isAuthenticated) {
    return <div className="page-container flex-1"><AdvisorConditionsPage type="Skip/NotLogin" /></div>;
  }

  const certs = data || [];

  return (
    <div className="page-container flex-1">
      <div className="flex flex-col gap-6">
        <div className="pb-4 border-b border-[#E2EBF3]">
          <p className="text-[1.5rem] font-[600] text-[#0184FF]">Certificates</p>
          <p className="text-[0.875rem] text-[#64748B] mt-0.5">Upload and manage your professional certificates</p>
        </div>

        {/* Upload area */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 flex flex-col gap-4">
          <p className="text-[0.9375rem] font-semibold text-[#0F172A]">Upload New Certificate</p>
          <input
            type="text"
            value={certName}
            onChange={(e) => setCertName(e.target.value)}
            placeholder="Certificate name (e.g. IRDAI Certification)"
            className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-[0.875rem] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0184FF] focus:border-transparent transition-shadow"
          />
          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 py-8 cursor-pointer transition-colors ${
              dragOver ? "border-[#0184FF] bg-[#EAF4FF]" : "border-[#CBD5E1] hover:border-[#0184FF] hover:bg-[#F8FAFC]"
            }`}
          >
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />
            <CloudUpload size={28} className={dragOver ? "text-[#0184FF]" : "text-[#94A3B8]"} />
            {uploadFile ? (
              <p className="text-[0.875rem] font-medium text-[#0184FF]">{uploadFile.name}</p>
            ) : (
              <>
                <p className="text-[0.875rem] font-medium text-[#475569]">Drag & drop or click to upload</p>
                <p className="text-[0.75rem] text-[#94A3B8]">PDF, JPG, PNG — max 5MB</p>
              </>
            )}
          </label>
          {uploadError && <p className="text-[0.8125rem] text-red-500">{uploadError}</p>}
          <button
            onClick={handleUpload}
            disabled={uploading || !uploadFile}
            className="self-start px-5 py-2.5 bg-[#0184FF] text-white text-[0.875rem] font-semibold rounded-lg hover:bg-[#006FD6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? "Uploading…" : "Upload Certificate"}
          </button>
        </div>

        {/* List */}
        <div className="flex flex-col gap-3">
          <p className="text-[0.9375rem] font-semibold text-[#0F172A]">
            Your Certificates {certs.length > 0 && <span className="text-[#64748B] font-normal">({certs.length})</span>}
          </p>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Array(4).fill(null).map((_, i) => <CertSkeleton key={i} />)}
            </div>
          ) : certs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4">
                <FileText size={28} className="text-[#94A3B8]" />
              </div>
              <p className="text-[0.9375rem] font-semibold text-[#0F172A]">No certificates yet</p>
              <p className="text-[0.8125rem] text-[#64748B] mt-1 max-w-xs">Upload your first professional certificate using the form above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {certs.map((cert, i) => (
                <div
                  key={cert.id || i}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-start gap-3 hover:border-[#0184FF] hover:shadow-[0_2px_8px_rgba(1,132,255,0.12)] transition-all duration-200"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#EAF4FF] flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-[#0184FF]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.875rem] font-semibold text-[#0F172A] truncate">{cert.certificate_name || cert.name || "Certificate"}</p>
                    <p className="text-[0.75rem] text-[#64748B] mt-0.5">
                      {cert.created_at ? new Date(cert.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <StatusBadge status={cert.status || cert.certificate_status} />
                      {cert.file_url && (
                        <a
                          href={cert.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[0.75rem] text-[#0184FF] hover:underline"
                        >
                          <Download size={12} /> View
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificatesPage;
